import { User, signInAnonymously } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../firebase';
import { InventoryCodeSession } from '../types';

const CODE_USER_NAME_STORAGE_KEY = 'tjcmed.inventoryCodeUserName';

interface RedeemResponse {
  eventId: string;
  eventLabel: string;
  expiresAt: number;
}

/**
 * 現場清點同工輸入梯次通行碼換取物資權限。
 *
 * 流程：匿名登入 → 呼叫 redeemInventoryCode → 後端寫入 custom claims →
 * 強制刷新 ID token，Firestore 規則才看得到新的 claims。
 */
export const redeemInventoryAccessCode = async (
  code: string,
  name: string
): Promise<InventoryCodeSession> => {
  if (!auth.currentUser) {
    await signInAnonymously(auth);
  }

  const callable = httpsCallable<{ code: string }, RedeemResponse>(functions, 'redeemInventoryCode');
  const response = await callable({ code });

  // claims 是在後端設定的，用戶端必須重新取得 token 才會帶上新權限。
  await auth.currentUser?.getIdToken(true);

  const session: InventoryCodeSession = {
    eventId: response.data.eventId,
    eventLabel: response.data.eventLabel,
    expiresAt: response.data.expiresAt,
    name,
  };

  rememberInventoryCodeUserName(name);
  return session;
};

/**
 * 重新整理後從既有的 ID token 還原通行碼身分，免得同工每次刷新都要重打一次碼。
 * 回傳 null 表示這個登入沒有有效的通行碼權限。
 */
export const restoreInventoryCodeSession = async (
  currentUser: User | null
): Promise<InventoryCodeSession | null> => {
  if (!currentUser?.isAnonymous) return null;

  try {
    const tokenResult = await currentUser.getIdTokenResult();
    const eventId = tokenResult.claims.invEvent;
    const expiresAt = tokenResult.claims.invExp;

    if (typeof eventId !== 'string' || !eventId) return null;
    if (typeof expiresAt !== 'number' || expiresAt <= Date.now()) return null;

    return {
      eventId,
      eventLabel: '',
      expiresAt,
      name: readInventoryCodeUserName(),
    };
  } catch (error) {
    console.error('Failed to restore inventory code session:', error);
    return null;
  }
};

/** 姓名只用來標記異動紀錄，留在本機就好，不必進 claims。 */
export const rememberInventoryCodeUserName = (name: string): void => {
  try {
    window.localStorage.setItem(CODE_USER_NAME_STORAGE_KEY, name);
  } catch {
    // 無痕模式或關閉儲存權限時忽略即可，只影響下次是否要重打姓名。
  }
};

export const readInventoryCodeUserName = (): string => {
  try {
    return window.localStorage.getItem(CODE_USER_NAME_STORAGE_KEY) || '';
  } catch {
    return '';
  }
};

export const forgetInventoryCodeUserName = (): void => {
  try {
    window.localStorage.removeItem(CODE_USER_NAME_STORAGE_KEY);
  } catch {
    // 同上，清不掉不影響權限。
  }
};
